import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { user, loading } = useAuth();
  const socketRef = useRef(null);

  useEffect(() => {
    // Attendre que le loading soit terminé
    if (loading) return;

    const token = localStorage.getItem('portalToken');
    
    // Si pas de token ou pas d'utilisateur, fermer la connexion existante
    if (!token || !user) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // Si déjà connecté avec le même token, ne rien faire
    if (socketRef.current?.connected) {
      return;
    }

    // Fermer l'ancienne connexion si elle existe
    if (socketRef.current) {
      socketRef.current.close();
    }

    // Créer la connexion socket
    const newSocket = io(window.location.origin, {
      auth: { token },
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      transports: ['polling', 'websocket'], // Polling d'abord, puis upgrade
      upgrade: true,
      forceNew: true
    });

    newSocket.on('connect', () => setConnected(true));
    newSocket.on('disconnect', () => setConnected(false));
    newSocket.on('connect_error', () => setConnected(false));

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setSocket(null);
      setConnected(false);
    };
  }, [user, loading]);

  // Fonction pour rejoindre une room de submission
  const joinSubmission = useCallback((submissionId) => {
    if (socket && submissionId) {
      socket.emit('joinSubmission', submissionId);
    }
  }, [socket]);

  // Fonction pour quitter une room de submission
  const leaveSubmission = useCallback((submissionId) => {
    if (socket && submissionId) {
      socket.emit('leaveSubmission', submissionId);
    }
  }, [socket]);

  const value = {
    socket,
    connected,
    joinSubmission,
    leaveSubmission
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

// Hook personnalisé pour utiliser le socket
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
